import React, { useState, useEffect } from 'react';
import { designerAPI, goalsAPI, notesAPI } from '../lib/api';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { 
  Target, 
  Plus, 
  Edit2, 
  Trash2, 
  CheckCircle,
  Clock,
  AlertCircle,
  AlertTriangle,
  FileText,
  Calendar,
  User,
  Save,
  MessageSquare,
  History,
  TrendingUp,
  Flag,
  ChevronDown,
  ChevronUp,
  Send
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Progress } from '../components/ui/progress';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { cn } from '../lib/utils';

const Goals = () => {
  const { isDark } = useTheme();
  const { user, isManager } = useAuth();
  const [designers, setDesigners] = useState([]);
  const [selectedDesigner, setSelectedDesigner] = useState('');
  const [goals, setGoals] = useState([]);
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dashboardStats, setDashboardStats] = useState(null);
  const [isGoalModalOpen, setIsGoalModalOpen] = useState(false);
  const [isNoteModalOpen, setIsNoteModalOpen] = useState(false);
  const [editingGoal, setEditingGoal] = useState(null);
  const [editingNote, setEditingNote] = useState(null);
  const [expandedGoal, setExpandedGoal] = useState(null);
  const [newComment, setNewComment] = useState('');
  
  const [goalForm, setGoalForm] = useState({
    title: '',
    description: '',
    target_score: '',
    deadline: '',
    status: 'not_started',
    progress_percentage: 0,
    milestone_status: 'not_started',
    impact_level: 'medium',
    impact_description: ''
  });
  
  const [noteForm, setNoteForm] = useState({
    date: new Date().toISOString().split('T')[0],
    summary: '',
    action_items: '',
    mood: 'neutral'
  });

  const STATUS_OPTIONS = [
    { value: 'not_started', label: 'Not Started', color: 'bg-slate-500', icon: Clock },
    { value: 'in_progress', label: 'In Progress', color: 'bg-blue-500', icon: TrendingUp },
    { value: 'review', label: 'Under Review', color: 'bg-yellow-500', icon: AlertCircle },
    { value: 'completed', label: 'Completed', color: 'bg-green-500', icon: CheckCircle },
    { value: 'cancelled', label: 'Cancelled', color: 'bg-red-500', icon: Trash2 },
  ];

  const MILESTONE_OPTIONS = [
    { value: 'not_started', label: 'Not Started' },
    { value: 'in_progress', label: 'In Progress' },
    { value: 'review', label: 'Under Review' },
    { value: 'completed', label: 'Completed' },
  ];

  const IMPACT_OPTIONS = [
    { value: 'low', label: 'Low', color: 'bg-slate-400 text-white' },
    { value: 'medium', label: 'Medium', color: 'bg-blue-500 text-white' },
    { value: 'high', label: 'High', color: 'bg-orange-500 text-white' },
    { value: 'critical', label: 'Critical', color: 'bg-red-600 text-white' },
  ];

  useEffect(() => {
    fetchDesigners();
    if (isManager()) {
      fetchDashboardStats();
    }
  }, []);

  useEffect(() => {
    if (selectedDesigner) {
      fetchGoals();
      fetchNotes();
    }
  }, [selectedDesigner]);

  const fetchDesigners = async () => {
    try {
      const response = await designerAPI.getAll();
      setDesigners(response.data);
      if (response.data.length > 0) {
        setSelectedDesigner(response.data[0].id);
      }
    } catch (error) {
      toast.error('Failed to load designers');
    } finally {
      setLoading(false);
    }
  };

  const fetchDashboardStats = async () => {
    try {
      const response = await goalsAPI.getDashboard();
      setDashboardStats(response.data);
    } catch (error) {
      console.error('Failed to load dashboard stats');
    }
  };

  const fetchGoals = async () => {
    try {
      const response = await goalsAPI.getAll(selectedDesigner);
      setGoals(response.data);
    } catch (error) {
      toast.error('Failed to load goals');
    }
  };

  const fetchNotes = async () => {
    try {
      const response = await notesAPI.getAll(selectedDesigner);
      setNotes(response.data);
    } catch (error) {
      toast.error('Failed to load notes');
    }
  };

  const handleSaveGoal = async () => {
    if (!goalForm.title.trim()) {
      toast.error('Goal title is required');
      return;
    }

    try {
      const goalData = {
        ...goalForm,
        designer_id: selectedDesigner,
        progress_percentage: parseInt(goalForm.progress_percentage) || 0,
      };

      if (editingGoal) {
        await goalsAPI.update(editingGoal.id, goalData);
        toast.success('Goal updated');
      } else {
        await goalsAPI.create(goalData);
        toast.success('Goal created');
      }

      setIsGoalModalOpen(false);
      resetGoalForm();
      fetchGoals();
      fetchDashboardStats();
    } catch (error) {
      toast.error('Failed to save goal');
    }
  };

  const handleDeleteGoal = async (goalId) => {
    if (!confirm('Are you sure you want to delete this goal?')) return;
    
    try {
      await goalsAPI.delete(goalId);
      toast.success('Goal deleted');
      fetchGoals();
      fetchDashboardStats();
    } catch (error) {
      toast.error('Failed to delete goal');
    }
  };

  const handleAddComment = async (goalId) => {
    if (!newComment.trim()) return;
    
    try {
      await goalsAPI.addComment(goalId, newComment);
      toast.success('Comment added');
      setNewComment('');
      fetchGoals();
    } catch (error) {
      toast.error('Failed to add comment');
    }
  };

  const handleQuickStatusUpdate = async (goalId, newStatus) => {
    try {
      const goal = goals.find(g => g.id === goalId);
      await goalsAPI.update(goalId, { ...goal, status: newStatus });
      toast.success('Status updated');
      fetchGoals();
      fetchDashboardStats();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleQuickProgressUpdate = async (goalId, newProgress) => {
    try {
      const goal = goals.find(g => g.id === goalId);
      await goalsAPI.update(goalId, { ...goal, progress_percentage: newProgress });
      fetchGoals();
    } catch (error) {
      toast.error('Failed to update progress');
    }
  };

  const resetGoalForm = () => {
    setGoalForm({
      title: '',
      description: '',
      target_score: '',
      deadline: '',
      status: 'not_started',
      progress_percentage: 0,
      milestone_status: 'not_started',
      impact_level: 'medium',
      impact_description: ''
    });
    setEditingGoal(null);
  };

  const openEditGoal = (goal) => {
    setEditingGoal(goal);
    setGoalForm({
      title: goal.title || '',
      description: goal.description || '',
      target_score: goal.target_score || '',
      deadline: goal.deadline || '',
      status: goal.status || 'not_started',
      progress_percentage: goal.progress_percentage || 0,
      milestone_status: goal.milestone_status || 'not_started',
      impact_level: goal.impact_level || 'medium',
      impact_description: goal.impact_description || ''
    });
    setIsGoalModalOpen(true);
  };

  const handleSaveNote = async () => {
    if (!noteForm.summary.trim()) {
      toast.error('Summary is required');
      return;
    }

    try {
      const noteData = {
        ...noteForm,
        designer_id: selectedDesigner,
      };

      if (editingNote) {
        await notesAPI.update(editingNote.id, noteData);
        toast.success('Note updated');
      } else {
        await notesAPI.create(noteData);
        toast.success('Note created');
      }

      setIsNoteModalOpen(false);
      setNoteForm({ date: new Date().toISOString().split('T')[0], summary: '', action_items: '', mood: 'neutral' });
      setEditingNote(null);
      fetchNotes();
    } catch (error) {
      toast.error('Failed to save note');
    }
  };

  const handleDeleteNote = async (noteId) => {
    if (!confirm('Delete this note?')) return;
    try {
      await notesAPI.delete(noteId);
      toast.success('Note deleted');
      fetchNotes();
    } catch (error) {
      toast.error('Failed to delete note');
    }
  };

  const getStatusInfo = (status) => STATUS_OPTIONS.find(s => s.value === status) || STATUS_OPTIONS[0];
  const getImpactInfo = (level) => IMPACT_OPTIONS.find(i => i.value === level) || IMPACT_OPTIONS[1];

  const selectedDesignerData = designers.find(d => d.id === selectedDesigner);

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="goals-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className={cn(
            "text-2xl md:text-3xl font-bold font-outfit flex items-center gap-3",
            isDark ? "text-white" : "text-slate-800"
          )}>
            <Target className="w-8 h-8 text-[#331062]" />
            Goals & 1-on-1 Notes
          </h1>
          <p className={cn("mt-1", isDark ? "text-slate-400" : "text-slate-500")}>
            Track goals, progress, and have meaningful conversations
          </p>
        </div>
        
        {/* Designer Selector */}
        <div className="w-full md:w-64">
          <Select value={selectedDesigner} onValueChange={setSelectedDesigner}>
            <SelectTrigger data-testid="designer-select">
              <User className="w-4 h-4 mr-2 text-slate-400" />
              <SelectValue placeholder="Select designer" />
            </SelectTrigger>
            <SelectContent>
              {designers.map(d => (
                <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Dashboard Stats (Managers only) */}
      {isManager() && dashboardStats && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
          <Card className={cn("border-l-4 border-l-blue-500", isDark ? "bg-slate-800 border-slate-700" : "")}>
            <CardContent className="p-3">
              <p className={cn("text-2xl font-bold", isDark ? "text-white" : "text-slate-800")}>{dashboardStats.total}</p>
              <p className={cn("text-xs", isDark ? "text-slate-400" : "text-slate-500")}>Total Goals</p>
            </CardContent>
          </Card>
          <Card className={cn("border-l-4 border-l-yellow-500", isDark ? "bg-slate-800 border-slate-700" : "")}>
            <CardContent className="p-3">
              <p className={cn("text-2xl font-bold", isDark ? "text-white" : "text-slate-800")}>{dashboardStats.in_progress}</p>
              <p className={cn("text-xs", isDark ? "text-slate-400" : "text-slate-500")}>In Progress</p>
            </CardContent>
          </Card>
          <Card className={cn("border-l-4 border-l-green-500", isDark ? "bg-slate-800 border-slate-700" : "")}>
            <CardContent className="p-3">
              <p className={cn("text-2xl font-bold", isDark ? "text-white" : "text-slate-800")}>{dashboardStats.completed}</p>
              <p className={cn("text-xs", isDark ? "text-slate-400" : "text-slate-500")}>Completed</p>
            </CardContent>
          </Card>
          <Card className={cn("border-l-4 border-l-red-500", isDark ? "bg-slate-800 border-slate-700" : "")}>
            <CardContent className="p-3">
              <p className={cn("text-2xl font-bold text-red-500", isDark ? "" : "")}>{dashboardStats.overdue}</p>
              <p className={cn("text-xs", isDark ? "text-slate-400" : "text-slate-500")}>Overdue</p>
            </CardContent>
          </Card>
          <Card className={cn("border-l-4 border-l-orange-500", isDark ? "bg-slate-800 border-slate-700" : "")}>
            <CardContent className="p-3">
              <p className={cn("text-2xl font-bold text-orange-500", isDark ? "" : "")}>{dashboardStats.approaching_deadline}</p>
              <p className={cn("text-xs", isDark ? "text-slate-400" : "text-slate-500")}>Due Soon</p>
            </CardContent>
          </Card>
          <Card className={cn("border-l-4 border-l-purple-500", isDark ? "bg-slate-800 border-slate-700" : "")}>
            <CardContent className="p-3">
              <p className={cn("text-2xl font-bold text-purple-500", isDark ? "" : "")}>{dashboardStats.critical_impact}</p>
              <p className={cn("text-xs", isDark ? "text-slate-400" : "text-slate-500")}>Critical</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tabs */}
      <Tabs defaultValue="goals" className="w-full">
        <TabsList className="mb-4">
          <TabsTrigger value="goals" className="flex items-center gap-2">
            <Target className="w-4 h-4" /> Goals ({goals.length})
          </TabsTrigger>
          <TabsTrigger value="notes" className="flex items-center gap-2">
            <FileText className="w-4 h-4" /> 1-on-1 Notes ({notes.length})
          </TabsTrigger>
        </TabsList>

        {/* Goals Tab */}
        <TabsContent value="goals">
          <div className="space-y-4">
            {/* Add Goal Button */}
            {isManager() && (
              <div className="flex justify-end">
                <Button 
                  onClick={() => { resetGoalForm(); setIsGoalModalOpen(true); }}
                  className="bg-[#331062] hover:bg-[#4a1d8a]"
                  data-testid="add-goal-btn"
                >
                  <Plus className="w-4 h-4 mr-2" /> Add Goal
                </Button>
              </div>
            )}

            {/* Goals List */}
            <AnimatePresence>
              {goals.length === 0 ? (
                <Card className={isDark ? "bg-slate-800 border-slate-700" : ""}>
                  <CardContent className="p-8 text-center">
                    <Target className={cn("w-12 h-12 mx-auto mb-4", isDark ? "text-slate-600" : "text-slate-300")} />
                    <p className={isDark ? "text-slate-400" : "text-slate-500"}>No goals set for this designer yet.</p>
                  </CardContent>
                </Card>
              ) : (
                goals.map((goal, index) => {
                  const statusInfo = getStatusInfo(goal.status);
                  const impactInfo = getImpactInfo(goal.impact_level);
                  const StatusIcon = statusInfo.icon;
                  const isExpanded = expandedGoal === goal.id;

                  return (
                    <motion.div
                      key={goal.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0 }}
                      transition={{ delay: index * 0.05 }}
                    >
                      <Card className={cn(
                        "overflow-hidden transition-all",
                        goal.is_overdue && "border-red-500 border-2",
                        goal.is_approaching && "border-orange-400 border-2",
                        isDark ? "bg-slate-800 border-slate-700" : ""
                      )}>
                        <CardContent className="p-4">
                          {/* Goal Header */}
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-2">
                                <span className={cn("px-2 py-0.5 rounded text-xs font-medium", statusInfo.color, "text-white")}>
                                  {statusInfo.label}
                                </span>
                                <span className={cn("px-2 py-0.5 rounded text-xs font-medium", impactInfo.color)}>
                                  {impactInfo.label} Impact
                                </span>
                                {goal.is_overdue && (
                                  <span className="px-2 py-0.5 rounded text-xs font-medium bg-red-600 text-white animate-pulse">
                                    OVERDUE
                                  </span>
                                )}
                                {goal.is_approaching && !goal.is_overdue && (
                                  <span className="px-2 py-0.5 rounded text-xs font-medium bg-orange-500 text-white">
                                    Due Soon
                                  </span>
                                )}
                              </div>
                              <h3 className={cn("font-semibold text-lg", isDark ? "text-white" : "text-slate-800")}>
                                {goal.title}
                              </h3>
                              {goal.description && (
                                <p className={cn("text-sm mt-1", isDark ? "text-slate-400" : "text-slate-600")}>
                                  {goal.description}
                                </p>
                              )}
                              <div className="flex flex-wrap items-center gap-4 mt-3 text-xs">
                                {goal.deadline && (
                                  <span className={cn("flex items-center gap-1", isDark ? "text-slate-400" : "text-slate-500")}>
                                    <Calendar className="w-3 h-3" /> Due: {goal.deadline}
                                  </span>
                                )}
                                {goal.assigned_by_name && (
                                  <span className={cn("flex items-center gap-1", isDark ? "text-slate-400" : "text-slate-500")}>
                                    <User className="w-3 h-3" /> Assigned by: {goal.assigned_by_name}
                                  </span>
                                )}
                              </div>
                            </div>
                            
                            {/* Actions */}
                            <div className="flex items-center gap-1">
                              {isManager() && (
                                <>
                                  <Button variant="ghost" size="icon" onClick={() => openEditGoal(goal)}>
                                    <Edit2 className="w-4 h-4 text-blue-500" />
                                  </Button>
                                  <Button variant="ghost" size="icon" onClick={() => handleDeleteGoal(goal.id)}>
                                    <Trash2 className="w-4 h-4 text-red-500" />
                                  </Button>
                                </>
                              )}
                              <Button variant="ghost" size="icon" onClick={() => setExpandedGoal(isExpanded ? null : goal.id)}>
                                {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                              </Button>
                            </div>
                          </div>

                          {/* Progress Bar */}
                          <div className="mt-4">
                            <div className="flex items-center justify-between mb-1">
                              <span className={cn("text-xs", isDark ? "text-slate-400" : "text-slate-500")}>
                                Progress: {goal.progress_percentage || 0}%
                              </span>
                              <span className={cn("text-xs", isDark ? "text-slate-400" : "text-slate-500")}>
                                Milestone: {MILESTONE_OPTIONS.find(m => m.value === goal.milestone_status)?.label || 'Not Started'}
                              </span>
                            </div>
                            <Progress value={goal.progress_percentage || 0} className="h-2" />
                            {isManager() && (
                              <input 
                                type="range" 
                                min="0" 
                                max="100" 
                                value={goal.progress_percentage || 0}
                                onChange={(e) => handleQuickProgressUpdate(goal.id, parseInt(e.target.value))}
                                className="w-full mt-1 h-1 cursor-pointer"
                              />
                            )}
                          </div>

                          {/* Expanded Section */}
                          <AnimatePresence>
                            {isExpanded && (
                              <motion.div
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: 'auto', opacity: 1 }}
                                exit={{ height: 0, opacity: 0 }}
                                className="mt-4 pt-4 border-t border-slate-200 dark:border-slate-700"
                              >
                                {/* Impact Description */}
                                {goal.impact_description && (
                                  <div className={cn("p-3 rounded-lg mb-4", isDark ? "bg-slate-700" : "bg-slate-50")}>
                                    <p className={cn("text-xs font-medium mb-1", isDark ? "text-slate-300" : "text-slate-600")}>
                                      <Flag className="w-3 h-3 inline mr-1" /> Impact if not met:
                                    </p>
                                    <p className={cn("text-sm", isDark ? "text-slate-400" : "text-slate-600")}>
                                      {goal.impact_description}
                                    </p>
                                  </div>
                                )}

                                {/* Quick Status Update */}
                                {isManager() && (
                                  <div className="mb-4">
                                    <p className={cn("text-xs font-medium mb-2", isDark ? "text-slate-300" : "text-slate-600")}>
                                      Quick Status Update:
                                    </p>
                                    <div className="flex flex-wrap gap-2">
                                      {STATUS_OPTIONS.filter(s => s.value !== 'cancelled').map(status => (
                                        <Button
                                          key={status.value}
                                          size="sm"
                                          variant={goal.status === status.value ? "default" : "outline"}
                                          onClick={() => handleQuickStatusUpdate(goal.id, status.value)}
                                          className={goal.status === status.value ? status.color : ""}
                                        >
                                          <status.icon className="w-3 h-3 mr-1" />
                                          {status.label}
                                        </Button>
                                      ))}
                                    </div>
                                  </div>
                                )}

                                {/* Comments Section */}
                                <div className="mb-4">
                                  <p className={cn("text-xs font-medium mb-2 flex items-center gap-1", isDark ? "text-slate-300" : "text-slate-600")}>
                                    <MessageSquare className="w-3 h-3" /> Comments & Updates ({goal.comments?.length || 0})
                                  </p>
                                  <div className="space-y-2 max-h-40 overflow-y-auto">
                                    {(goal.comments || []).map(comment => (
                                      <div key={comment.id} className={cn("p-2 rounded text-sm", isDark ? "bg-slate-700" : "bg-slate-100")}>
                                        <div className="flex items-center justify-between mb-1">
                                          <span className={cn("font-medium text-xs", isDark ? "text-slate-300" : "text-slate-700")}>
                                            {comment.user_name}
                                          </span>
                                          <span className={cn("text-xs", isDark ? "text-slate-500" : "text-slate-400")}>
                                            {new Date(comment.created_at).toLocaleDateString()}
                                          </span>
                                        </div>
                                        <p className={isDark ? "text-slate-400" : "text-slate-600"}>{comment.comment}</p>
                                      </div>
                                    ))}
                                  </div>
                                  {/* Add Comment */}
                                  <div className="flex gap-2 mt-2">
                                    <Input
                                      placeholder="Add a comment or update..."
                                      value={expandedGoal === goal.id ? newComment : ''}
                                      onChange={(e) => setNewComment(e.target.value)}
                                      onKeyPress={(e) => e.key === 'Enter' && handleAddComment(goal.id)}
                                      className="flex-1"
                                    />
                                    <Button size="icon" onClick={() => handleAddComment(goal.id)} className="bg-[#331062]">
                                      <Send className="w-4 h-4" />
                                    </Button>
                                  </div>
                                </div>

                                {/* History Section */}
                                <div>
                                  <p className={cn("text-xs font-medium mb-2 flex items-center gap-1", isDark ? "text-slate-300" : "text-slate-600")}>
                                    <History className="w-3 h-3" /> History ({goal.history?.length || 0})
                                  </p>
                                  <div className="space-y-1 max-h-32 overflow-y-auto">
                                    {(goal.history || []).slice().reverse().map(entry => (
                                      <div key={entry.id} className={cn("text-xs flex items-center gap-2", isDark ? "text-slate-400" : "text-slate-500")}>
                                        <span className="w-24 flex-shrink-0">{new Date(entry.timestamp).toLocaleDateString()}</span>
                                        <span className="font-medium">{entry.user_name}</span>
                                        <span>-</span>
                                        <span>{entry.action.replace(/_/g, ' ')}</span>
                                        {entry.old_value && entry.new_value && (
                                          <span>({entry.old_value} → {entry.new_value})</span>
                                        )}
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </CardContent>
                      </Card>
                    </motion.div>
                  );
                })
              )}
            </AnimatePresence>
          </div>
        </TabsContent>

        {/* Notes Tab */}
        <TabsContent value="notes">
          <div className="space-y-4">
            {isManager() && (
              <div className="flex justify-end">
                <Button 
                  onClick={() => setIsNoteModalOpen(true)}
                  className="bg-[#331062] hover:bg-[#4a1d8a]"
                >
                  <Plus className="w-4 h-4 mr-2" /> Add Note
                </Button>
              </div>
            )}

            {notes.length === 0 ? (
              <Card className={isDark ? "bg-slate-800 border-slate-700" : ""}>
                <CardContent className="p-8 text-center">
                  <FileText className={cn("w-12 h-12 mx-auto mb-4", isDark ? "text-slate-600" : "text-slate-300")} />
                  <p className={isDark ? "text-slate-400" : "text-slate-500"}>No 1-on-1 notes for this designer yet.</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {notes.map((note, index) => (
                  <motion.div
                    key={note.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                  >
                    <Card className={isDark ? "bg-slate-800 border-slate-700" : ""}>
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between">
                          <div className="flex items-center gap-3">
                            <div className={cn(
                              "w-3 h-3 rounded-full",
                              note.mood === 'positive' && "bg-green-500",
                              note.mood === 'neutral' && "bg-yellow-500",
                              note.mood === 'needs_attention' && "bg-red-500"
                            )} />
                            <div>
                              <span className={cn("text-sm font-medium", isDark ? "text-white" : "text-slate-800")}>
                                {note.date}
                              </span>
                              <span className={cn("ml-2 text-xs px-2 py-0.5 rounded", 
                                note.mood === 'positive' && "bg-green-100 text-green-700",
                                note.mood === 'neutral' && "bg-yellow-100 text-yellow-700",
                                note.mood === 'needs_attention' && "bg-red-100 text-red-700"
                              )}>
                                {note.mood === 'positive' ? 'Positive' : note.mood === 'neutral' ? 'Neutral' : 'Needs Attention'}
                              </span>
                            </div>
                          </div>
                          {isManager() && (
                            <div className="flex gap-1">
                              <Button variant="ghost" size="icon" onClick={() => { setEditingNote(note); setNoteForm(note); setIsNoteModalOpen(true); }}>
                                <Edit2 className="w-4 h-4 text-blue-500" />
                              </Button>
                              <Button variant="ghost" size="icon" onClick={() => handleDeleteNote(note.id)}>
                                <Trash2 className="w-4 h-4 text-red-500" />
                              </Button>
                            </div>
                          )}
                        </div>
                        <p className={cn("mt-3", isDark ? "text-slate-300" : "text-slate-600")}>{note.summary}</p>
                        {note.action_items && (
                          <div className={cn("mt-3 p-3 rounded-lg", isDark ? "bg-slate-700" : "bg-slate-50")}>
                            <p className={cn("text-xs font-medium mb-1", isDark ? "text-slate-300" : "text-slate-600")}>Action Items:</p>
                            <p className={cn("text-sm", isDark ? "text-slate-400" : "text-slate-600")}>{note.action_items}</p>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  </motion.div>
                ))}
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>

      {/* Goal Modal */}
      <Dialog open={isGoalModalOpen} onOpenChange={setIsGoalModalOpen}>
        <DialogContent className={cn("max-w-lg max-h-[90vh] overflow-y-auto", isDark && "bg-slate-800 border-slate-700")}>
          <DialogHeader>
            <DialogTitle className={isDark ? "text-white" : ""}>
              {editingGoal ? 'Edit Goal' : 'Add New Goal'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Title *</Label>
              <Input
                value={goalForm.title}
                onChange={(e) => setGoalForm({ ...goalForm, title: e.target.value })}
                placeholder="e.g., Complete Advanced Figma Certification"
              />
            </div>
            <div>
              <Label>Description</Label>
              <Textarea
                value={goalForm.description}
                onChange={(e) => setGoalForm({ ...goalForm, description: e.target.value })}
                placeholder="Goal details..."
                rows={2}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Deadline</Label>
                <Input
                  type="date"
                  value={goalForm.deadline}
                  onChange={(e) => setGoalForm({ ...goalForm, deadline: e.target.value })}
                />
              </div>
              <div>
                <Label>Status</Label>
                <Select value={goalForm.status} onValueChange={(v) => setGoalForm({ ...goalForm, status: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {STATUS_OPTIONS.map(s => (
                      <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Progress ({goalForm.progress_percentage}%)</Label>
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={goalForm.progress_percentage}
                  onChange={(e) => setGoalForm({ ...goalForm, progress_percentage: parseInt(e.target.value) })}
                  className="w-full mt-2"
                />
              </div>
              <div>
                <Label>Milestone</Label>
                <Select value={goalForm.milestone_status} onValueChange={(v) => setGoalForm({ ...goalForm, milestone_status: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {MILESTONE_OPTIONS.map(m => (
                      <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Impact Level if Not Met</Label>
              <Select value={goalForm.impact_level} onValueChange={(v) => setGoalForm({ ...goalForm, impact_level: v })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {IMPACT_OPTIONS.map(i => (
                    <SelectItem key={i.value} value={i.value}>{i.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Impact Description</Label>
              <Textarea
                value={goalForm.impact_description}
                onChange={(e) => setGoalForm({ ...goalForm, impact_description: e.target.value })}
                placeholder="Describe the consequences if this goal is not met..."
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsGoalModalOpen(false)}>Cancel</Button>
            <Button onClick={handleSaveGoal} className="bg-[#331062] hover:bg-[#4a1d8a]">
              <Save className="w-4 h-4 mr-2" /> {editingGoal ? 'Update' : 'Create'} Goal
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Note Modal */}
      <Dialog open={isNoteModalOpen} onOpenChange={setIsNoteModalOpen}>
        <DialogContent className={cn("max-w-lg", isDark && "bg-slate-800 border-slate-700")}>
          <DialogHeader>
            <DialogTitle className={isDark ? "text-white" : ""}>
              {editingNote ? 'Edit Note' : 'Add 1-on-1 Note'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Date</Label>
                <Input
                  type="date"
                  value={noteForm.date}
                  onChange={(e) => setNoteForm({ ...noteForm, date: e.target.value })}
                />
              </div>
              <div>
                <Label>Mood</Label>
                <Select value={noteForm.mood} onValueChange={(v) => setNoteForm({ ...noteForm, mood: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="positive">Positive</SelectItem>
                    <SelectItem value="neutral">Neutral</SelectItem>
                    <SelectItem value="needs_attention">Needs Attention</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Summary *</Label>
              <Textarea
                value={noteForm.summary}
                onChange={(e) => setNoteForm({ ...noteForm, summary: e.target.value })}
                placeholder="Discussion summary..."
                rows={3}
              />
            </div>
            <div>
              <Label>Action Items</Label>
              <Textarea
                value={noteForm.action_items}
                onChange={(e) => setNoteForm({ ...noteForm, action_items: e.target.value })}
                placeholder="Follow-up actions..."
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setIsNoteModalOpen(false); setEditingNote(null); }}>Cancel</Button>
            <Button onClick={handleSaveNote} className="bg-[#331062] hover:bg-[#4a1d8a]">
              <Save className="w-4 h-4 mr-2" /> Save Note
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Goals;
